import { insertImage } from '@platejs/media';
import { ImagePlugin } from '@platejs/media/react';
import {
  PButton,
  PCheckbox,
  PCheckboxProps,
  PInlineNotification,
} from '@porsche-design-system/components-react';
import { useEditorState } from 'platejs/react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useDropzone } from 'react-dropzone';

import ImageIcon from '../../../../../icons/image.svg?react';
import { ActionGroup } from '../../../ActionGroup';
import { Dropzone, DropzoneFiles, DropzoneInner } from '../../../Dropzone';
import { Modal } from '../../../Modal';
import { Spacer } from '../../../Spacer';
import { ToolbarBlockButton } from '../../ToolbarBlockButton';

export const ImageToolbarButtons = ({
  onUploadImage,
  onCancelImageUpload,
  allowedImageTypes,
  i18n,
  allowPublicImageUpload,
}: {
  onUploadImage: (
    fileOrData: File | string,
    opt: {
      uploadAsPubliclyAccessible?: boolean;
      onProgress?: (value: number) => void;
    },
  ) => Promise<string>;
  onCancelImageUpload?: () => void;
  allowedImageTypes?: { type: string; extension: string }[];
  i18n?: {
    imageUploadHeading?: string;
    uploadButtonText?: string;
    resetButtonText?: string;
    uploadErrorHeading?: string;
    uploadErrorDescription?: string;
    dropzoneDescription?: string;
    allowedTypesDescription?: string;
    dragDescription?: string;
    publicImageUploadText?: string;
  };
  allowPublicImageUpload: 'required' | 'allowed' | 'disallowed';
}) => {
  const editor = useEditorState();

  const [imageUploadModalOpen, setImageUploadModalOpen] = useState(false);
  const [uploadImageProgress, setUploadImageProgress] = useState(0);

  return (
    <>
      <ToolbarBlockButton
        icon={ImageIcon}
        type={ImagePlugin.key}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          setImageUploadModalOpen(true);
        }}
        disabledOnActive
        aria-label={i18n?.uploadButtonText}
      />
      <ImageUploadModal
        open={imageUploadModalOpen}
        onClose={() => {
          if (onCancelImageUpload) {
            onCancelImageUpload();
          }
          setImageUploadModalOpen(false);
        }}
        onSubmit={(file, options) =>
          onUploadImage(file, {
            onProgress: setUploadImageProgress,
            ...options,
          })
            .then((url) => {
              setImageUploadModalOpen(false);
              setUploadImageProgress(0);

              insertImage(editor, url);
              editor.tf.focus();
            })
            .catch(() => {
              setImageUploadModalOpen(false);
              setUploadImageProgress(0);
            })
        }
        publicImageUploadText={i18n?.publicImageUploadText}
        allowPublicImageUpload={allowPublicImageUpload}
        uploadProgress={uploadImageProgress}
        allowedTypes={allowedImageTypes ?? []}
        heading={i18n?.imageUploadHeading ?? ''}
        uploadButtonText={i18n?.uploadButtonText}
        resetButtonText={i18n?.resetButtonText}
        uploadErrorHeading={i18n?.uploadErrorHeading ?? ''}
        uploadErrorDescription={i18n?.uploadErrorDescription}
        dropzoneDescription={i18n?.dropzoneDescription ?? ''}
        allowedTypesDescription={i18n?.allowedTypesDescription ?? ''}
        dragDescription={i18n?.dragDescription ?? ''}
      />
    </>
  );
};

const ImageUploadModal = ({
  open,
  allowPublicImageUpload,
  onClose,
  onSubmit,
  uploadProgress,
  heading,
  dropzoneDescription,
  dragDescription,
  allowedTypesDescription,
  publicImageUploadText,
  uploadButtonText,
  resetButtonText,
  uploadErrorHeading,
  uploadErrorDescription,
  allowedTypes,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    file: File,
    options: { uploadAsPubliclyAccessible: boolean },
  ) => void;
  allowPublicImageUpload: 'required' | 'allowed' | 'disallowed';
  uploadProgress: number;
  heading: string;
  dropzoneDescription: string;
  dragDescription: string;
  allowedTypesDescription: string;
  publicImageUploadText?: string;
  uploadButtonText: ReactNode;
  resetButtonText: ReactNode;
  uploadErrorHeading: string;
  uploadErrorDescription: ReactNode;
  allowedTypes: { type: string; extension: string }[];
}) => {
  const [file, setFile] = useState<File | undefined>();
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [uploadAsPubliclyAccessible, setUploadAsPubliclyAccessible] =
    useState(false);

  const onDropAccepted = useCallback((acceptedFiles: File[]) => {
    const nextFile = acceptedFiles[0];
    setFile(nextFile);
  }, []);

  const onDropRejected = useCallback(() => {
    setIsErrorVisible(true);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted,
    onDropRejected,
    maxFiles: 1,
    multiple: false,
    useFsAccessApi: false,
    accept: allowedTypes.reduce(
      (memo, { type, extension }) => ({ ...memo, [type]: [`.${extension}`] }),
      {},
    ),
  });

  useEffect(() => {
    if (!open) {
      setFile(undefined);
    }
  }, [open]);

  return (
    <Modal open={open} onDismiss={onClose} disableBackdropClick>
      <Modal.Header>{heading}</Modal.Header>
      {isErrorVisible && (
        <>
          <PInlineNotification
            state="error"
            heading={uploadErrorHeading}
            onDismiss={() => setIsErrorVisible(false)}
          >
            {uploadErrorDescription}
          </PInlineNotification>

          <Spacer mb="sm" />
        </>
      )}
      <Dropzone {...getRootProps()}>
        <input {...getInputProps()} />
        <DropzoneInner
          isDragActive={isDragActive}
          i18n={{
            description: dropzoneDescription,
            dragDescription,
            allowedTypes: allowedTypesDescription,
          }}
        />
      </Dropzone>
      {file && (
        <DropzoneFiles
          files={[
            {
              id: file.name,
              file,
            },
          ]}
          progress={[uploadProgress]}
          onDelete={() => {
            setFile(undefined);
          }}
        />
      )}
      {allowPublicImageUpload !== 'disallowed' && (
        <>
          <Spacer mb="md" />

          <PCheckbox
            label={publicImageUploadText}
            checked={
              allowPublicImageUpload === 'required' ||
              uploadAsPubliclyAccessible
            }
            disabled={allowPublicImageUpload === 'required'}
            onChange={(event) => {
              const { checked } = event.target as HTMLElement & PCheckboxProps;
              setUploadAsPubliclyAccessible(checked);
            }}
          />
        </>
      )}
      <ActionGroup>
        <PButton
          type="button"
          aria-label={resetButtonText}
          variant="secondary"
          icon="close"
          onClick={() => onClose()}
        >
          {resetButtonText}
        </PButton>
        <PButton
          type="button"
          disabled={!file}
          aria-label={uploadButtonText}
          onClick={() =>
            file &&
            onSubmit(file, {
              uploadAsPubliclyAccessible:
                allowPublicImageUpload === 'required'
                  ? true
                  : allowPublicImageUpload === 'disallowed'
                    ? false
                    : uploadAsPubliclyAccessible,
            })
          }
        >
          {uploadButtonText}
        </PButton>
      </ActionGroup>
    </Modal>
  );
};
